import React from 'react';
import {
    Card,
    CardContent,
    Box,
    Typography,
    Grid,
    Divider,
    LinearProgress,
    alpha,
    useTheme
} from '@mui/material';
import {
    PieChart,
    Pie,
    Cell,
    ResponsiveContainer,
    Tooltip as RechartsTooltip
} from 'recharts';

const PortfolioMetrics = ({ portfolioData }) => {
    const theme = useTheme();

    if (!portfolioData) {
        return null;
    }

    // Property metrics data for pie chart
    const getPropertyMetrics = () => {
        if (!portfolioData || !portfolioData.properties || !portfolioData.occupancyMap) {
            return [];
        }

        console.log("Properties:", portfolioData.properties);
        console.log("Occupancy:", portfolioData.occupancyMap);

        return portfolioData.properties.map(property => ({
            name: property.property_name,
            units: property.total_property_units || 1, // Fallback to 1 if missing
            value: property.total_property_units || 1, // Fallback for pie chart
            occupancy: portfolioData.occupancyMap[property.id] || 0, // Use occupancyMap
            expectedRevenue: property.expected_revenue || 0
        }));
    };

    // Colors for pie chart
    const COLORS = [
        theme.palette.primary.main,
        theme.palette.secondary.main,
        theme.palette.success.main,
        theme.palette.warning.main,
        theme.palette.info.main,
        theme.palette.error.main
    ];

    return (
        <Card
            elevation={0}
            sx={{
                height: '100%',
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`
            }}
        >
            <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                    Portfolio Metrics
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                                Total Properties
                            </Typography>
                            <Typography variant="h5" fontWeight="bold">
                                {portfolioData.totalProperties || 0}
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={6}>
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                                Total Units
                            </Typography>
                            <Typography variant="h5" fontWeight="bold">
                                {portfolioData.totalUnits || 0}
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={6}>
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                                Occupied Units
                            </Typography>
                            <Typography variant="h5" fontWeight="bold">
                                {portfolioData.occupiedUnits || 0}
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={6}>
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                                Vacant Units
                            </Typography>
                            <Typography variant="h5" fontWeight="bold">
                                {portfolioData.vacantUnits || (portfolioData.totalUnits - portfolioData.occupiedUnits) || 0}
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Overall Occupancy Rate
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ flex: 1, mr: 2 }}>
                            <LinearProgress
                                variant="determinate"
                                value={portfolioData.occupancy || 0}
                                sx={{
                                    height: 10,
                                    borderRadius: 5,
                                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                                    '& .MuiLinearProgress-bar': {
                                        bgcolor: portfolioData.occupancy > 80
                                            ? theme.palette.success.main
                                            : portfolioData.occupancy > 50
                                                ? theme.palette.warning.main
                                                : theme.palette.error.main
                                    }
                                }}
                            />
                        </Box>
                        <Typography variant="h6" fontWeight="bold">
                            {(portfolioData.occupancy || 0).toFixed(1)}%
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ height: 165, display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            <Pie
                                data={getPropertyMetrics()}
                                cx="50%"
                                cy="50%"
                                innerRadius={50}
                                outerRadius={80}
                                fill="#8884d8"
                                paddingAngle={2}
                                dataKey="value"
                                nameKey="name"
                                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                labelLine={false}
                            >
                                {getPropertyMetrics().map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Pie>
                            <RechartsTooltip
                                formatter={(value, name) => [`${value} units`, name]}
                            />
                        </PieChart>
                    </ResponsiveContainer>
                </Box>
            </CardContent>
        </Card>
    );
};

export default PortfolioMetrics;